import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "./ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable "Latest blog articles" teaser strip.
 * Used on HomePage, QuiSuisJePage (À propos), etc. to strengthen internal SEO linking.
 * Props:
 *   - title (string, default "Articles du blog")
 *   - subtitle (string)
 *   - limit (number, default 3)
 *   - category (string, optional) — restrict to a category
 */
export default function LatestBlogTeaser({
  title = "Articles du blog",
  subtitle = "Inspirations, guides et rituels pour t'accompagner.",
  limit = 3,
  category = null,
}) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const params = { limit };
    if (category) params.category = category;
    axios.get(`${API}/blog/articles`, { params })
      .then(res => { if (mounted) setArticles(res.data.articles || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [limit, category]);

  if (!loading && articles.length === 0) return null;

  return (
    <section className="py-20 relative" data-testid="latest-blog-teaser">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 text-[#00E5FF] text-xs uppercase tracking-widest mb-3">
            <Sparkles className="w-4 h-4" /> Le journal
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mb-2 text-white">{title}</h2>
          <p className="text-[#B59ED1] max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: limit }).map((_, i) => (
                <div key={i} className="h-72 bg-[#261B3B]/60 rounded-2xl animate-pulse" />
              ))
            : articles.map(a => (
                <article
                  key={a.article_id || a.slug}
                  onClick={() => navigate(`/blog/${a.slug}`)}
                  className="group bg-[#261B3B] rounded-2xl overflow-hidden border border-white/10 hover:border-[#D926A9]/50 transition-all duration-300 cursor-pointer flex flex-col"
                  data-testid={`teaser-card-${a.slug}`}
                >
                  {a.image_url && (
                    <div className="aspect-video overflow-hidden bg-[#1B132B]">
                      <img
                        src={a.image_url.startsWith("/") ? `${BACKEND_URL}${a.image_url}` : a.image_url}
                        alt={a.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <Badge className="bg-[#D926A9]/20 text-[#D926A9] w-fit mb-3">{a.category}</Badge>
                    <h3 className="font-serif text-lg mb-2 text-[#F7F3FC] group-hover:text-[#00E5FF] transition-colors line-clamp-2">
                      {a.title}
                    </h3>
                    <p className="text-sm text-[#B59ED1] line-clamp-3 flex-1">{a.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-[#00E5FF] text-sm font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                      Lire <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </article>
              ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#00E5FF] hover:text-white transition-colors text-sm font-medium"
            data-testid="teaser-see-all"
          >
            Tous les articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
